import { EditOutlined } from "@ant-design/icons";
import { Button, Modal, Space, Layout, Form, Checkbox } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { BASE_PATH } from "../../../..";
import { IllustrationCameraPointDTO, SceneCameraDTO, SceneIllustrationDTO } from "../../../../backendapi/requests";
import { Content } from "antd/es/layout/layout";
import Sider from "antd/es/layout/Sider";
import "./IllustrationEditor.scss"

const IllustrationEditorButton: React.FC<{ cameras: SceneCameraDTO[], value?: SceneIllustrationDTO, onChange?: (value: SceneIllustrationDTO) => void }> = ({ onChange, value, cameras }) => {
    const [isEditorOpen, setIsEditorOpen] = useState(false);

    const showEditor = useCallback(() => {
        setIsEditorOpen(true);
    }, []);

    const hideEditor = useCallback(() => {
        setIsEditorOpen(false);
    }, []);

    const onEditorChange = useCallback((val: SceneIllustrationDTO) => {
        setIsEditorOpen(false);
        onChange?.(val);
    }, [onChange]);

    return (
        <Space>
            <Button onClick={showEditor} icon={<EditOutlined />}></Button>
            <IllustrationEditor cameras={cameras} isOpen={isEditorOpen} value={value!} onCancel={hideEditor} onChange={onEditorChange} />
        </Space>
    );
}

const IllustrationEditor: React.FC<{ cameras: SceneCameraDTO[], isOpen: boolean, value: SceneIllustrationDTO, onCancel: () => void, onChange: (value: SceneIllustrationDTO) => void }> = ({ cameras, isOpen, value, onCancel, onChange }) => {
    const [currentValue, setCurrentValue] = useState<IllustrationCameraPointDTO[]>(value?.cameraPlacementPoints || []);
    const containerRef = useRef<HTMLDivElement>(null);
    const [selectedPoint, setSelectedPoint] = useState<IllustrationCameraPointDTO | null>(null);
    const [isRotating, setIsRotating] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setCurrentValue(value?.cameraPlacementPoints || []);
        }
    }, [isOpen, value])

    const handleOk = useCallback(() => {
        onChange({ ...value, cameraPlacementPoints: currentValue });
    }, [currentValue, onChange, value]);

    const handleCancel = useCallback(() => {
        onCancel();
    }, [onCancel]);

    const handleCameraClick = useCallback((cam: SceneCameraDTO) => {
        if (currentValue.some(e => e.cameraId === cam.id)) {
            setCurrentValue(currentValue.filter(e => e.cameraId !== cam.id))
        } else {
            setCurrentValue([...currentValue, { cameraId: cam.id, posX: 0, posY: 0, rotation: 0 }]);
        }
    }, [currentValue]);

    const handleStartDrag = (e: React.MouseEvent, item: IllustrationCameraPointDTO) => {
        e.preventDefault();
        e.stopPropagation();

        setIsRotating(false);
        setSelectedPoint(item)
    }

    const handleStartDragRotate = (e: React.MouseEvent, item: IllustrationCameraPointDTO) => {
        e.preventDefault();
        e.stopPropagation();

        setIsRotating(true);
        setSelectedPoint(item)
    }

    const handleDrag = (e: React.MouseEvent) => {
        if (e.preventDefault) {
            e.preventDefault();
        }

        if (selectedPoint) {
            if (containerRef.current) {
                var rect = containerRef.current.getBoundingClientRect();
                var mouseX = ((e.clientX - rect.left) / rect.width);
                var mouseY = ((e.clientY - rect.top) / rect.height);

                if (isRotating) {
                    setCurrentValue(val => val.map(e => e.cameraId === selectedPoint.cameraId ? { ...e, rotation: Math.atan2(mouseY - e.posY, mouseX - e.posX) * (180 / Math.PI) + 180 } : e));
                } else {
                    setCurrentValue(val => val.map(e => e.cameraId === selectedPoint.cameraId ? { ...e, posX: mouseX, posY: mouseY } : e));
                }
            }
        }
    }

    const handleDragEnd = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (selectedPoint) {
            setSelectedPoint(null)
        }
    }

    return (
        <Modal title="Edit illustration" width={1200} open={isOpen} onOk={handleOk} onCancel={handleCancel}>
            <Layout>
                <Sider style={{ padding: "20px" }} width={200}>
                    <Form layout="vertical" >
                        {cameras.map(cam =>
                            <Form.Item>
                                <Checkbox checked={currentValue.some(e => e.cameraId === cam.id)} onChange={() => handleCameraClick(cam)}>{cam.label}</Checkbox>
                            </Form.Item>
                        )}
                    </Form>
                </Sider>
                <Content>
                    <div className="illustration-editor" ref={containerRef} onMouseMove={handleDrag} onMouseUp={handleDragEnd}>
                        <img src={`${BASE_PATH}${value?.image}`} alt="sceneCameraImage" />
                        {currentValue.map((item) => (
                            <div className="cam" key={item.cameraId} style={{ top: `${item.posY * 100}%`, left: `${item.posX * 100}%` }} draggable onDragStart={(e) => handleStartDrag(e, item)}>
                                <div className="direction" style={{ transform: `rotate(${item.rotation}deg)` }}>
                                    <div className="direction-cone"></div>
                                    <div className="direction-handle" draggable onDragStart={(e) => handleStartDragRotate(e, item)} ></div>
                                </div>
                                <div className="label">{cameras.find(e => e.id === item.cameraId)?.label}</div>
                            </div>
                        ))}
                    </div>
                </Content>
            </Layout>
        </Modal>
    )
}

export default IllustrationEditorButton;